// Site copy for Nimbus. Keep every claim here true to what the backend does.

import {
  ShieldCheck, Sparkles, Monitor, GitBranch, Users,
  FileSpreadsheet, Database, FileText, Boxes, LineChart,
  Briefcase, FlaskConical, HardHat, HeartPulse, Scale, Wallet,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Platform", href: "#platform" },
  { label: "How it works", href: "#how" },
  { label: "Use cases", href: "#usecases" },
  { label: "Roles", href: "#roles" },
  { label: "Pricing", href: "#pricing" },
];

export const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Independent Auditor",
    desc: "After every run, the auditor checks each figure in the report against the numbers the tools actually produced. VERIFIED only when every figure traces back; otherwise PARTIAL, with the untraced figures listed.",
    tag: "Trust",
  },
  {
    icon: FileSpreadsheet,
    title: "Real data tools",
    desc: "Profiles, filters, aggregates, de-duplicates and reconciles CSV, Excel and JSON files, and searches PDFs and text, using real pandas operations on your data instead of guesses.",
    tag: "Accuracy",
  },
  {
    icon: Sparkles,
    title: "Reusable Skills",
    desc: "Save instructions you repeat (your rules, column names, checks) as a named skill and reuse them on any run, privately or with your team.",
    tag: "Automation",
  },
  {
    icon: GitBranch,
    title: "Fast & Complex modes",
    desc: "Fast mode allows up to 3 tool calls for quick answers. Complex mode allows up to 8 for multi-step work like reconciliations.",
    tag: "Control",
  },
  {
    icon: Users,
    title: "Shared workspaces",
    desc: "Create a team, invite members by email and share runs, files and skills inside it.",
    tag: "Teamwork",
  },
  {
    icon: Monitor,
    title: "Full step log",
    desc: "Every run keeps the list of tools the agent called, why, and what each returned, alongside the audit and your saved run history.",
    tag: "Transparency",
  },
];

export const STEPS = [
  {
    n: "01",
    title: "Describe the task",
    desc: "Attach up to 5 files (CSV, Excel, JSON, PDF, TXT) and type the goal in plain language, or pick a saved skill.",
    img: "https://images.unsplash.com/photo-1743385779347-1549dabf1320?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    n: "02",
    title: "The agent runs real tools",
    desc: "The model chooses data tools (profile, filter, aggregate, dedupe, reconcile, search) and Nimbus runs them on your files. Each step is logged.",
    img: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    n: "03",
    title: "Audit every figure",
    desc: "The auditor traces each number in the report to a tool result and labels the run VERIFIED, PARTIAL or ESTIMATED.",
    img: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export const USE_CASES = [
  {
    icon: FileSpreadsheet,
    title: "Spreadsheet checks",
    desc: "Profile, clean up and de-duplicate CSV and Excel files, and total or group any column.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    icon: Database,
    title: "Reconciliation",
    desc: "Match two files on a key, list missing records on each side and flag amount differences.",
    img: "https://images.unsplash.com/photo-1535957998253-26ae1ef29506?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    icon: FileText,
    title: "Document search",
    desc: "Find clauses, terms and figures in text-based PDFs and text files, with the matching passages quoted.",
    img: "https://images.pexels.com/photos/8636589/pexels-photo-8636589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const ROLES = [
  { icon: Briefcase, name: "Operations", tasks: "Reconciliations, record counts, reporting" },
  { icon: LineChart, name: "Data Science", tasks: "Profiling, aggregation, dedupe" },
  { icon: Wallet, name: "Finance", tasks: "Invoice vs ledger matching, totals" },
  { icon: Scale, name: "Legal", tasks: "Clause search in contract PDFs" },
  { icon: HeartPulse, name: "Healthcare admin", tasks: "Claims spreadsheet checks" },
  { icon: HardHat, name: "Energy & Field ops", tasks: "Field log spreadsheet summaries" },
  { icon: FlaskConical, name: "Research", tasks: "Dataset prep and profiling" },
  { icon: Boxes, name: "Supply Chain", tasks: "Order and inventory reconciliation" },
];

export const PRICING = [
  {
    name: "Free",
    price: "₹0",
    inr: "Free everywhere",
    period: "/ 30 days",
    blurb: "For individuals working with their own files.",
    features: ["50 agent runs per 30 days", "Pick your model (Opus in Complex mode counts as 3)", "Fast & Complex modes", "Auditor on every run", "Skills and team workspaces"],
    cta: "Start free",
    highlight: false,
  },
  {
    // Starter and Pro: price, runs and model are filled in from GET /api/pricing (Gemini 2.5 Flash only).
    name: "Starter",
    planId: "starter_pass",
    price: "",
    inr: "",
    period: "/ month",
    blurb: "For regular individual use. Renews monthly until you cancel in Settings.",
    features: [],
    cta: "Subscribe (UPI or card)",
    highlight: false,
  },
  {
    name: "Pro",
    planId: "pro_pass",
    price: "",
    inr: "",
    period: "/ month",
    blurb: "For heavy individual use. Renews monthly until you cancel in Settings.",
    features: [],
    cta: "Subscribe (UPI or card)",
    highlight: false,
  },
  {
    // Price, runs and model are filled in from GET /api/pricing (the numbers checkout charges).
    name: "Team",
    price: "",
    inr: "",
    period: "/ month per person",
    blurb: "For heavier use. Renews monthly until you cancel in Settings.",
    features: [],
    cta: "Subscribe (UPI or card)",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    blurb: "Need higher limits or help getting set up? Talk to us.",
    features: ["Custom run limits", "Onboarding help"],
    cta: "Contact us",
    highlight: false,
  },
];

export const FAQS = [
  { q: "What can Nimbus actually do?", a: "It runs data tools on files you attach: profile, filter, aggregate, find duplicates, reconcile two files and search text in PDFs or TXT files. It has no browser, desktop or internet access and doesn't edit your files." },
  { q: "How does the auditor work?", a: "It pulls every number out of the final report and checks it against the numbers the tools actually returned. If every figure traces back, the run is VERIFIED; if some don't, it's PARTIAL and those figures are listed; with no data at all, the output is an ESTIMATED draft." },
  { q: "Which files are supported?", a: "CSV, XLSX, XLS, JSON, text-based PDF and TXT, up to the upload size limit. Scanned PDFs and images aren't supported yet (no OCR)." },
  { q: "Can I see what the agent did?", a: "Yes. Each run lists the tools it called, why, and a summary of each result. Runs, steps and audits are saved to your account or team." },
  { q: "Do all runs count the same?", a: "On Free you pick the model, and a run with Claude Opus in Complex mode counts as 3 runs because it costs far more; every other run counts as 1. On Starter and Pro, every run uses Gemini 2.5 Flash and counts as 1; each run has a fixed AI budget, and a run too large for it is refused before it starts without using one of your runs. On Team, every run uses Gemini 2.5 Flash-Lite and counts as 1." },
  { q: "Is my data private?", a: "Runs and files are private to your account unless you share them in a team workspace. File contents are sent to the AI model you pick only as tool results." },
];

// Sample tasks for the interactive Workspace demo
export const SAMPLE_TASKS = [
  "Reconcile the attached invoices against the ledger and flag mismatches",
  "Profile this CSV: row count, empty cells and duplicate rows",
  "Total the amount column by vendor and list the top 5",
  "Find the termination and renewal clauses in this contract PDF",
];

// LLM model options exposed in the workspace picker (matches backend providers)
export const LLM_MODELS = [
  {
    provider: "openai",
    label: "OpenAI",
    models: [
      { id: "gpt-5.4", name: "GPT-5.4", tag: "Recommended" },
      { id: "gpt-5.4-mini", name: "GPT-5.4 Mini", tag: "Fast" },
      { id: "gpt-4o", name: "GPT-4o", tag: "" },
    ],
  },
  {
    provider: "anthropic",
    label: "Anthropic",
    models: [
      { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", tag: "Recommended" },
      { id: "claude-opus-4-7", name: "Claude Opus 4.7", tag: "Deep · Complex runs count as 3", runWeightComplex: 3 },
    ],
  },
  {
    provider: "gemini",
    label: "Google",
    models: [
      { id: "gemini-3.1-pro-preview", name: "Gemini 3.1 Pro", tag: "Recommended" },
      { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash", tag: "Fast" },
    ],
  },
];
